/**
 * Folder access assignment (customer admins only).
 *
 * Folders were historically a flat global namespace — every supplier saw every
 * folder in the filter picker, which leaked other suppliers' codes and part
 * numbers through folder names alone. This dialog is how a customer admin
 * decides which suppliers may see a folder.
 *
 * Reuses VisibilityField so folder access and document access read identically:
 *   private -> is_public false, shared_with_companies empty (customer-side only)
 *   shared  -> is_public false, shared_with_companies = the chosen suppliers
 *   public  -> is_public true (every supplier on the platform)
 *
 * The backend enforces the same customer-admin gate, so a supplier admin who
 * reaches this endpoint directly still gets a 403.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { documentsAPI } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { VisibilityField } from './VisibilityField';
import { DocumentVisibility } from './types';

interface FolderAccessDialogProps {
  category: {
    id: number | null;
    name: string;
    is_public?: boolean;
    shared_with_companies?: number[];
  };
  onClose: () => void;
  onSaved: () => void;
}

/** Derive the three-state picker value from the folder's stored flags. */
function initialVisibility(
  is_public?: boolean,
  shared_with_companies?: number[]
): DocumentVisibility {
  if (is_public) return 'public';
  return (shared_with_companies?.length ?? 0) > 0 ? 'shared' : 'private';
}

export function FolderAccessDialog({ category, onClose, onSaved }: FolderAccessDialogProps) {
  const [visibility, setVisibility] = useState<DocumentVisibility>('private');
  const [sharedCompanyIds, setSharedCompanyIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  // The folder rows this dialog is opened from come from the documents
  // by_category endpoint, whose payload carries no is_public /
  // shared_with_companies. Seeding state from the passed-in row therefore
  // always read as "Private" regardless of the real access, and saving that
  // back silently REVOKED every existing share — opening the dialog to look
  // at who had access was enough to destroy it. Always load the folder's
  // actual state from the category endpoint before the form is usable.
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!category.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    documentsAPI
      .getCategory(category.id)
      .then((res) => {
        if (cancelled) return;
        const { is_public, shared_with_companies } = res.data;
        setVisibility(initialVisibility(is_public, shared_with_companies));
        setSharedCompanyIds(shared_with_companies ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        // Never fall through to an empty form — saving it would revoke.
        setLoadError(true);
        toast.error('Could not load current folder access');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [category.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard the write as well as the UI: submitting before the current access
    // has loaded would send an empty share list and revoke everything.
    if (!category.id || loading || loadError) return;
    setSaving(true);
    try {
      await documentsAPI.updateCategory(category.id, {
        is_public: visibility === 'public',
        // Clearing the list is meaningful (revoking access), so always send it.
        shared_with_companies: visibility === 'shared' ? sharedCompanyIds : [],
      });
      toast.success(`Access updated for "${category.name}"`);
      onSaved();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = status === 403
        ? 'Only customer admins can change folder access.'
        : getApiErrorMessage(err, 'Failed to update folder access');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Folder access — {category.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Suppliers only see folders shared with them. A folder is also visible
            to a supplier when it already holds a document they can access.
          </p>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading current access…
            </div>
          ) : loadError ? (
            <p className="text-sm text-red-600 py-6">
              Could not load this folder&rsquo;s current access, so it cannot be
              edited safely. Close the dialog and try again.
            </p>
          ) : (
            <VisibilityField
              visibility={visibility}
              onVisibilityChange={setVisibility}
              sharedCompanyIds={sharedCompanyIds}
              onSharedCompanyIdsChange={setSharedCompanyIds}
              subjectNoun="folder"
            />
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || loading || loadError}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save access
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
